import queryString from 'query-string';
import React, {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {isTablet} from '../../breakpoints';
import {TeamMembers} from '../../components/MembersTable/TeamMembersTable';
import {TeamMemberRole} from '../../components/MembersTable/TeamMembersTable/utils';
import {PageWithSidebar} from '../../components/PageWithSidebar';
import ProjectsTableTeam from '../../components/ProjectsTableTeam';
import {
  QuickStart,
  QuickStartAnalyticsOrigin,
} from '../../components/QuickStart';
import RoutedTab from '../../components/RoutedTab';
import {AccountSelectorContext} from '../../components/Search/SearchNav/AccountSelectorContextProvider';
import {ServiceAccountsTable} from '../../components/ServiceAccounts/ServiceAccounts';
import {TeamSettings} from '../../components/TeamSettings';
import TeamTrialNudgeBar from '../../components/TeamTrialNudgeBar';
import config from '../../config';
import {WorkflowsAdminType} from '../../generated/graphql';
import {TEAM_PAGE_QUERY} from '../../graphql/teamPage';
import {TeamPageEntity} from '../../graphql/teamQuery';
import {useQuery} from '../../state/graphql/query';
import {useViewer} from '../../state/viewer/hooks';
import {PremiumFeatureTooltip} from '../../util/accounts/pricing';
import {useAdminModeActive} from '../../util/admin';
import {setDocumentTitle} from '../../util/document';
import {safeLocalStorage} from '../../util/localStorage';
import {teamPage} from '../../util/urls';
import {LoaderProxy} from './../../containers/AppLoader';
import TeamInviteModal from './TeamInviteModal';
import {TeamOverview} from './TeamOverview';
import * as S from './TeamPage.styles';
import {TeamSidebar} from './TeamSidebar';
import {TeamPageQueryData, TeamPageQueryVariables} from './types';

interface TeamPageProps {
  entityName: string;
  tab?: string;
}

export const TeamPage: FC<TeamPageProps> = React.memo(({entityName, tab}) => {
  const [inviteQueryParam, setInviteQueryParam] = useState('');
  const [isNewTeam, setIsNewTeam] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  const tabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDocumentTitle(entityName);
  }, [entityName]);

  useLayoutEffect(() => {
    if (tab === 'members' && isTablet()) {
      tabRef.current?.scrollIntoView?.();
    }
  }, [tab]);

  const teamPageQuery = useQuery<TeamPageQueryData, TeamPageQueryVariables>(
    TEAM_PAGE_QUERY,
    {
      variables: {entityName, workflowsAdminType: WorkflowsAdminType.Model},
    }
  );

  const data: TeamPageEntity | null =
    teamPageQuery.loading || !teamPageQuery.data?.entity
      ? null
      : teamPageQuery.data?.entity;

  useEffect(() => {
    const {
      invited: invitedParam,
      newUser: newUserParam,
      newTeam: newTeamParam,
    }: queryString.ParsedQuery<string> = queryString.parse(
      document.location.search
    );
    // On first render, if the URL indicates that the user is accepting
    // the team invite, open the modal to update default entity
    if (!!invitedParam && inviteQueryParam === '') {
      const invited: string =
        typeof invitedParam === 'string' ? invitedParam : invitedParam[0];
      safeLocalStorage.setItem('auth.invited', invited);
      setInviteQueryParam(invited);

      window.analytics?.track('Invite Accepted', {
        entityName,
        location: 'team page',
      });
    }

    if (newUserParam) {
      const isNew =
        typeof newUserParam === 'string'
          ? newUserParam === 'true'
          : newUserParam[0] === 'true';
      setIsNewUser(isNew);
    }

    if (newTeamParam) {
      setIsNewTeam(true);
    }
  }, [entityName, inviteQueryParam]);

  const viewer = useViewer();
  const isAdminUser = useAdminModeActive();
  const isViewerAdmin =
    isAdminUser ||
    data?.members?.some(
      m => m.username === viewer?.username && m.role === TeamMemberRole.ADMIN
    );
  const shouldShowSettingsTab = isViewerAdmin;

  const {selectedAccount} = useContext(AccountSelectorContext);
  // technically you should have a valid license to use service accounts in onprem environments,
  // but since you can't create teams without a license anyway, checking the config is fine for now until we
  // get account selector in server
  const isEnterpriseAccount =
    selectedAccount?.isEnterprise || config.ENVIRONMENT_IS_PRIVATE;

  if (teamPageQuery.loading || data == null) {
    return <LoaderProxy name="team-page-loader" />;
  }

  const {name: teamName, readOnly: isReadOnly} = data;

  // by checking for _at least_ one project, we know it's not a new team
  const projectCount = teamPageQuery.data?.entity.projectCount;

  const showQuickstart =
    !isReadOnly &&
    projectCount === 0 &&
    !['members', 'settings'].includes(tab ?? 'overview');

  return (
    <S.Wrapper>
      <PageWithSidebar
        sidebar={
          <TeamSidebar
            members={data.members}
            teamData={data}
            tabRef={tabRef}
            refetch={teamPageQuery.refetch}
          />
        }
        contentStyle={{
          backgroundColor: showQuickstart ? 'white' : 'unset',
        }}>
        <>
          {showQuickstart ? (
            <div style={{width: '100%'}}>
              <TeamTrialNudgeBar entityName={entityName} />
              <QuickStart
                analyticsOrigin={QuickStartAnalyticsOrigin.TEAM_PAGE}
              />
            </div>
          ) : (
            <div ref={tabRef} style={{maxWidth: '1000px'}}>
              <RoutedTab
                baseUrl={teamPage(teamName)}
                tabSlugs={[
                  'overview',
                  'projects',
                  'members',
                  'service-accounts',
                  ...(shouldShowSettingsTab ? ['settings'] : []),
                ]}
                activeTabSlug={tab}
                panes={[
                  {
                    menuItem: 'Overview',
                    render: () => (
                      <TeamOverview
                        entityName={teamName}
                        isReadOnly={isReadOnly}
                      />
                    ),
                  },
                  {
                    menuItem: 'Projects',
                    render: () => (
                      <ProjectsTableTeam
                        entityName={teamName}
                        isReadOnly={isReadOnly}
                      />
                    ),
                  },
                  {
                    menuItem: 'Members',
                    render: () => (
                      <TeamMembers
                        entity={data}
                        refetch={teamPageQuery.refetch}
                      />
                    ),
                  },
                  {
                    menuItem: {
                      key: 'service-accounts',
                      content: (
                        <>
                          Service Accounts &nbsp;
                          <PremiumFeatureTooltip
                            isPremiumFeatureDisabled={!isEnterpriseAccount}
                          />
                        </>
                      ),
                    },
                    render: () => (
                      <ServiceAccountsTable
                        entity={data}
                        // TODO(elaina): we should not refetch the entire team page query here
                        refetchTeamData={teamPageQuery.refetch}
                      />
                    ),
                  },
                  ...(shouldShowSettingsTab
                    ? [
                        {
                          menuItem: 'Settings',
                          render: () => <TeamSettings entityName={teamName} />,
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
          {(inviteQueryParam || isNewTeam || isNewUser) && (
            <TeamInviteModal
              inviteQueryParam={inviteQueryParam}
              isNewUser={isNewUser}
              isNewTeam={isNewTeam}
              setInviteQueryParam={setInviteQueryParam}
              teamName={teamName}
            />
          )}
        </>
      </PageWithSidebar>
    </S.Wrapper>
  );
});
